import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { MoreHorizontal, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * Shared state + mutations + rename/delete dialogs for a single workspace.
 * Returns:
 *   - openRename(), openDelete(): action openers
 *   - dialogs: JSX to place ONCE next to the trigger
 *   - t, i18n
 * Used by both `WorkspaceKebab` (⋯ button) and `WorkspaceContextMenu` (right-click).
 */
function useWorkspaceActions(workspace) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const params = useParams();

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [name, setName] = useState(workspace.name);

  const renameMutation = useMutation({
    mutationFn: async (newName) =>
      (await api.patch(`/workspaces/${workspace.workspace_id}`, {
        name: newName, color: workspace.color, description: workspace.description || "",
      })).data,
    onSuccess: () => {
      toast.success(t("workspace.renameSuccess", "Workspace renombrado"));
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      setRenameOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("workspace.renameError", "No se pudo renombrar")),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => (await api.delete(`/workspaces/${workspace.workspace_id}`)).data,
    onSuccess: () => {
      toast.success(t("workspace.deleteSuccess", "Workspace eliminado"));
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
      setDeleteOpen(false);
      if (params.workspaceId === workspace.workspace_id) navigate("/");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("workspace.deleteError", "No se pudo eliminar")),
  });

  const submitRename = () => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error(t("workspace.renameEmpty", "El nombre no puede estar vacío")); return; }
    if (trimmed === workspace.name) { setRenameOpen(false); return; }
    renameMutation.mutate(trimmed);
  };

  const dialogs = (
    <>
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent data-testid={`workspace-rename-dialog-${workspace.workspace_id}`}>
          <DialogHeader>
            <DialogTitle>{t("workspace.renameTitle", "Renombrar workspace")}</DialogTitle>
            <DialogDescription>
              {t("workspace.renameDesc", "Cambia el nombre visible del workspace.")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); submitRename(); }
                if (e.key === "Escape") setRenameOpen(false);
              }}
              maxLength={60}
              data-testid={`workspace-rename-input-${workspace.workspace_id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>{t("common.cancel", "Cancelar")}</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={renameMutation.isPending}
              onClick={submitRename}
              data-testid={`workspace-rename-save-${workspace.workspace_id}`}
            >
              {renameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save", "Guardar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid={`workspace-delete-dialog-${workspace.workspace_id}`}>
          <DialogHeader>
            <DialogTitle className="text-rose-500">
              {t("workspace.deleteTitle", "Eliminar workspace")}
            </DialogTitle>
            <DialogDescription>
              {t("workspace.deleteDesc", "Se eliminarán permanentemente todos los tableros, listas, tarjetas, comentarios y adjuntos dentro de este workspace. Esta acción no se puede deshacer.")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm">
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 px-3 py-2">
              <span className="text-muted-foreground">Workspace:</span>{" "}
              <strong className="text-foreground">{workspace.name}</strong>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>{t("common.cancel", "Cancelar")}</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              data-testid={`workspace-delete-confirm-${workspace.workspace_id}`}
            >
              {deleteMutation.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><Trash2 className="h-4 w-4 mr-1.5" /> {t("workspace.deleteConfirm", "Eliminar definitivamente")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    t,
    openRename: () => { setName(workspace.name); setRenameOpen(true); },
    openDelete: () => setDeleteOpen(true),
    dialogs,
  };
}

/** Kebab (⋯) menu button. */
export default function WorkspaceKebab({ workspace, className, iconClassName }) {
  const { t, openRename, openDelete, dialogs } = useWorkspaceActions(workspace);
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              "h-6 w-6 grid place-items-center rounded-md hover:bg-white/60 dark:hover:bg-white/10 text-muted-foreground hover:text-foreground transition",
              className
            )}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            aria-label={t("workspace.actions", "Acciones del workspace")}
            data-testid={`workspace-kebab-${workspace.workspace_id}`}
          >
            <MoreHorizontal className={cn("h-3.5 w-3.5", iconClassName)} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem onSelect={openRename}
                            data-testid={`workspace-rename-${workspace.workspace_id}`}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            {t("workspace.rename", "Renombrar")}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={openDelete}
                            className="text-rose-500 focus:text-rose-600"
                            data-testid={`workspace-delete-${workspace.workspace_id}`}>
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {t("workspace.delete", "Eliminar")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </>
  );
}

/**
 * Right-click wrapper: wraps its children in a native context menu that opens
 * the same 2 actions (rename / delete). Use it around the workspace tile / link.
 */
export function WorkspaceContextMenu({ workspace, children, asChild = true }) {
  const { t, openRename, openDelete, dialogs } = useWorkspaceActions(workspace);
  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild={asChild}
                            data-testid={`workspace-ctx-trigger-${workspace.workspace_id}`}>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-40">
          <ContextMenuItem onSelect={openRename}
                           data-testid={`workspace-ctx-rename-${workspace.workspace_id}`}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            {t("workspace.rename", "Renombrar")}
          </ContextMenuItem>
          <ContextMenuItem onSelect={openDelete}
                           className="text-rose-500 focus:text-rose-600"
                           data-testid={`workspace-ctx-delete-${workspace.workspace_id}`}>
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            {t("workspace.delete", "Eliminar")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      {dialogs}
    </>
  );
}
